import {
  AlertTriangle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  TrendingUp,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Select } from "../components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import { apiService } from "../services/api.service";
import type { ErrorAnalysisItem, ErrorAnalysisSummary } from "../types/admin.types";
import { RETRYABLE_ERROR_CODES, STEP_DISPLAY_NAMES, type StepName } from "../types/admin.types";

const PAGE_SIZE = 20;

/**
 * エラーコードがリトライ可能かどうかを判定
 */
function isRetryable(errorCode: string): boolean {
  return RETRYABLE_ERROR_CODES.includes(errorCode as (typeof RETRYABLE_ERROR_CODES)[number]);
}

/**
 * 日次エラー発生率推移のデータ型
 */
type DailyErrorData = {
  date: string;
  count: number;
};

/**
 * 日次エラー発生率推移チャートコンポーネント
 */
function ErrorRateTrendChart({ data }: { data: DailyErrorData[] }) {
  if (data.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-gray-400">データがありません</div>
    );
  }

  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="flex h-40 items-end gap-1">
      {data.map((item) => {
        const height = (item.count / maxCount) * 100;
        return (
          <div className="group relative flex flex-1 flex-col items-center" key={item.date}>
            <div
              className="w-full rounded-t bg-red-500 transition-all group-hover:bg-red-600"
              style={{ height: `${Math.max(height, 2)}%` }}
            />
            <span className="mt-1 text-gray-500 text-xs">
              {new Date(item.date).toLocaleDateString("ja-JP", {
                month: "numeric",
                day: "numeric",
              })}
            </span>
            {/* Tooltip */}
            <div className="-top-8 -translate-x-1/2 pointer-events-none absolute left-1/2 z-10 hidden rounded bg-gray-800 px-2 py-1 text-white text-xs group-hover:block">
              {item.count}件
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ErrorsPage() {
  const [items, setItems] = useState<ErrorAnalysisItem[]>([]);
  const [summary, setSummary] = useState<ErrorAnalysisSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedErrorCode, setSelectedErrorCode] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(0);
  const [dailyTrend, setDailyTrend] = useState<DailyErrorData[]>([]);

  // 日次エラー発生率の計算（過去14日間）
  useEffect(() => {
    if (items.length === 0 && total === 0) {
      setDailyTrend([]);
      return;
    }

    // 過去14日間の日付を生成
    const days: DailyErrorData[] = [];
    for (let i = 13; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push({
        date: date.toISOString().split("T")[0],
        count: 0,
      });
    }

    // itemsから日付ごとのカウントを計算
    for (const item of items) {
      const itemDate = item.createdAt.split("T")[0];
      const dayData = days.find((d) => d.date === itemDate);
      if (dayData) {
        dayData.count++;
      }
    }

    setDailyTrend(days);
  }, [items, total]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await apiService.getErrorAnalysis(
          undefined,
          undefined,
          selectedErrorCode || undefined,
          PAGE_SIZE,
          currentPage * PAGE_SIZE,
        );
        setItems(response.items);
        setTotal(response.total);
        setHasMore(response.hasMore);
        setSummary(response.summary);
      } catch (err) {
        setError(err instanceof Error ? err.message : "データの取得に失敗しました");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedErrorCode, currentPage]);

  // リトライ可/不可の分類
  const retryableCount =
    summary?.byErrorCode.reduce(
      (acc, item) => acc + (isRetryable(item.code) ? item.count : 0),
      0,
    ) ?? 0;
  const nonRetryableCount = (summary?.totalErrors ?? 0) - retryableCount;
  const retryablePercentage =
    summary && summary.totalErrors > 0 ? (retryableCount / summary.totalErrors) * 100 : 0;

  if (isLoading && items.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
    );
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <h1 className="font-bold text-2xl text-gray-900">エラー分析</h1>

      {/* サマリー */}
      {summary && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="flex items-center gap-4 py-6">
                <div className="rounded-full bg-red-100 p-3">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">総エラー数</p>
                  <p className="font-bold text-2xl">{summary.totalErrors}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center gap-4 py-6">
                <div className="rounded-full bg-orange-100 p-3">
                  <TrendingUp className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">最多エラー</p>
                  <p className="font-bold text-lg">{summary.byErrorCode[0]?.code ?? "-"}</p>
                  <p className="text-gray-500 text-sm">
                    {summary.byErrorCode[0]?.count ?? 0}件 (
                    {summary.byErrorCode[0]?.percentage?.toFixed(1) ?? 0}%)
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center gap-4 py-6">
                <div className="rounded-full bg-green-100 p-3">
                  <RefreshCw className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">リトライ可能</p>
                  <p className="font-bold text-lg">{retryableCount}件</p>
                  <p className="text-gray-500 text-sm">({retryablePercentage.toFixed(1)}%)</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center gap-4 py-6">
                <div className="rounded-full bg-purple-100 p-3">
                  <XCircle className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm">リトライ不可</p>
                  <p className="font-bold text-lg">{nonRetryableCount}件</p>
                  <p className="text-gray-500 text-sm">
                    ({(100 - retryablePercentage).toFixed(1)}%)
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* エラー発生率推移グラフ */}
          <Card>
            <CardHeader>
              <CardTitle>エラー発生率推移（過去14日間）</CardTitle>
            </CardHeader>
            <CardContent>
              <ErrorRateTrendChart data={dailyTrend} />
            </CardContent>
          </Card>

          {/* エラーパターン分類 */}
          <Card>
            <CardHeader>
              <CardTitle>エラーパターン分類</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* リトライ可能 */}
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 text-green-600" />
                    <h3 className="font-semibold text-green-800">リトライ可能なエラー</h3>
                  </div>
                  <div className="space-y-2">
                    {summary.byErrorCode
                      .filter((item) => isRetryable(item.code))
                      .map((item) => (
                        <div className="flex items-center justify-between" key={item.code}>
                          <span className="text-green-700 text-sm">{item.code}</span>
                          <span className="font-medium text-green-800">{item.count}件</span>
                        </div>
                      ))}
                    {summary.byErrorCode.filter((item) => isRetryable(item.code)).length === 0 && (
                      <p className="text-gray-500 text-sm">該当なし</p>
                    )}
                  </div>
                </div>

                {/* リトライ不可 */}
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <h3 className="font-semibold text-red-800">リトライ不可のエラー</h3>
                  </div>
                  <div className="space-y-2">
                    {summary.byErrorCode
                      .filter((item) => !isRetryable(item.code))
                      .map((item) => (
                        <div className="flex items-center justify-between" key={item.code}>
                          <span className="text-red-700 text-sm">{item.code}</span>
                          <span className="font-medium text-red-800">{item.count}件</span>
                        </div>
                      ))}
                    {summary.byErrorCode.filter((item) => !isRetryable(item.code)).length === 0 && (
                      <p className="text-gray-500 text-sm">該当なし</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* エラーコード別分布 */}
          <Card>
            <CardHeader>
              <CardTitle>エラーコード別分布</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {summary.byErrorCode.map((item) => (
                  <div className="flex items-center gap-4" key={item.code}>
                    <button
                      className={`flex items-center gap-2 rounded px-3 py-1 font-medium text-sm transition-colors ${
                        selectedErrorCode === item.code
                          ? "bg-red-600 text-white"
                          : "bg-red-100 text-red-800 hover:bg-red-200"
                      }
                      `}
                      onClick={() =>
                        setSelectedErrorCode(selectedErrorCode === item.code ? "" : item.code)
                      }
                      type="button"
                    >
                      {item.code}
                      {isRetryable(item.code) ? (
                        <RefreshCw className="h-3 w-3" />
                      ) : (
                        <XCircle className="h-3 w-3" />
                      )}
                    </button>
                    <div className="h-4 flex-1 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full rounded-full bg-red-500 transition-all duration-500"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                    <span className="w-24 text-right text-gray-600 text-sm">
                      {item.count}件 ({item.percentage.toFixed(1)}%)
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ステップ別分布 */}
          <Card>
            <CardHeader>
              <CardTitle>失敗ステップ別分布</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {summary.byStep.map((item) => (
                  <div className="rounded-lg bg-gray-50 p-4 text-center" key={item.step}>
                    <p className="text-gray-500 text-sm">
                      {STEP_DISPLAY_NAMES[item.step as StepName] ?? item.step}
                    </p>
                    <p className="mt-1 font-bold text-2xl text-gray-900">{item.count}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* エラー一覧 */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-4">
            <CardTitle>エラー詳細一覧</CardTitle>
            <span className="text-gray-500 text-sm">
              {total}件中 {currentPage * PAGE_SIZE + 1}-
              {Math.min((currentPage + 1) * PAGE_SIZE, total)}件を表示
            </span>
          </div>
          <div className="flex items-center gap-4">
            {/* エラーコードフィルタ */}
            <Select
              className="w-48"
              onChange={(e) => {
                const value = e.target.value;
                setSelectedErrorCode(value === "all" ? "" : value);
                setCurrentPage(0);
              }}
              options={[
                { value: "all", label: "すべてのエラー" },
                ...(summary?.byErrorCode.map((item) => ({
                  value: item.code,
                  label: `${item.code} (${item.count}件)`,
                })) ?? []),
              ]}
              value={selectedErrorCode || "all"}
            />
            {selectedErrorCode && (
              <Badge variant="error">
                フィルタ: {selectedErrorCode}
                <button
                  className="ml-2 hover:text-red-900"
                  onClick={() => {
                    setSelectedErrorCode("");
                    setCurrentPage(0);
                  }}
                  type="button"
                >
                  ×
                </button>
              </Badge>
            )}
          </div>
        </CardHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>エラーコード</TableHead>
              <TableHead>ステップ</TableHead>
              <TableHead>候補者</TableHead>
              <TableHead>求人</TableHead>
              <TableHead>チーム / 会社</TableHead>
              <TableHead>エラーメッセージ</TableHead>
              <TableHead>発生日時</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell className="py-8 text-center text-gray-500" colSpan={8}>
                  エラーデータがありません
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant="error">{item.errorCode}</Badge>
                      {isRetryable(item.errorCode) ? (
                        <RefreshCw aria-label="リトライ可能" className="h-3 w-3 text-green-600" />
                      ) : (
                        <XCircle aria-label="リトライ不可" className="h-3 w-3 text-red-600" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.stepName ? (
                      <Badge variant="default">
                        {STEP_DISPLAY_NAMES[item.stepName as StepName] ?? item.stepName}
                      </Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>{item.candidateName ?? "名前未取得"}</TableCell>
                  <TableCell>
                    <p className="max-w-32 truncate" title={item.jobPostingTitle}>
                      {item.jobPostingTitle}
                    </p>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p className="font-medium">{item.teamName}</p>
                      <p className="text-gray-500">{item.companyName}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p
                      className="max-w-48 truncate text-gray-600 text-sm"
                      title={item.errorMessage}
                    >
                      {item.errorMessage}
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{new Date(item.createdAt).toLocaleString("ja-JP")}</p>
                  </TableCell>
                  <TableCell>
                    <Link
                      className="text-blue-600 hover:text-blue-800"
                      to={`/admin/skill-checks/${item.id}`}
                    >
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* ページネーション */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <p className="text-gray-500 text-sm">
              全{total}件中 {currentPage * PAGE_SIZE + 1}-
              {Math.min((currentPage + 1) * PAGE_SIZE, total)}件を表示
            </p>
            <div className="flex items-center gap-2">
              <Button
                disabled={currentPage === 0}
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                size="sm"
                variant="outline"
              >
                <ChevronLeft className="h-4 w-4" />
                前へ
              </Button>
              <span className="text-gray-600 text-sm">
                {currentPage + 1} / {totalPages}
              </span>
              <Button
                disabled={!hasMore}
                onClick={() => setCurrentPage((p) => p + 1)}
                size="sm"
                variant="outline"
              >
                次へ
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
